/**
 * @description Renders the Filters form of the assign to contract page
 */

import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import withErrorBoundary from '@seccon/shared/ui/hocs/with-error-boundary';

import DropdownField from './DropdownField';
import SearchPersonnelTextField from './SearchPersonnelTextfield';
import { SetFiltersContext } from '../contexts/filters';
import useFacilityOptions from '../hooks/useFacilityOptions';
import useEligibilityOptions from '../hooks/useEligibilityOptions';
import useSpecialAccessOptions from '../hooks/useSpecialAccessOptions';

const styles = {
  actionButton: {
    minWidth: '100px'
  },
  container: {
    backgroundColor: 'grey.100',
    padding: 2
  },
  fieldsContainer: {
    display: 'flex',
    gap: 2,
    justifyContent: 'stretch',
    width: '100%',

    '& > *': {
      flex: 1,

      '&.personnel': {
        flex: 2
      }
    }
  },
  actionsContainer: {
    display: 'flex',
    gap: 2,
    justifyContent: 'flex-end',
    width: '100%'
  }
}

const Filters = () => {
  const setFilters = React.useContext(SetFiltersContext);
  const eligibilityOptions = useEligibilityOptions();
  const specialAccessOptions = useSpecialAccessOptions();
  const facilityOptions = useFacilityOptions();

  const form = useForm();

  /**
   * Handles submission of the filter form by saving it to the form context state
   * @param {object} data - form data
   */
  function handleSubmit (data) {
    setFilters(data);
  }

  /**
   * Handles clearing of the filter form by resetting the form and clearing the form context state
   */
  function clearSearch () {
    form.reset();
    setFilters(null);
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Paper elevation={2} sx={styles.container}>
          <Stack spacing={2}>
            <Typography variant='h5'>Filter Personnel</Typography>
            <Box sx={styles.fieldsContainer}>
              <DropdownField
                id='eligibility'
                isError={eligibilityOptions.isError}
                isLoading={eligibilityOptions.isLoading}
                label='Eligibility'
                multiple
                options={eligibilityOptions.data}
                variant='filters'
              />
              <DropdownField
                id='specialAccesses'
                isError={specialAccessOptions.isError}
                isLoading={specialAccessOptions.isLoading}
                label='Special Accesses'
                multiple
                options={specialAccessOptions.data}
                variant='filters'
              />
              <SearchPersonnelTextField />
              <DropdownField
                id='facilityIds'
                isError={facilityOptions.isError}
                isLoading={facilityOptions.isLoading}
                label='Facility'
                multiple
                options={facilityOptions.data}
                variant='filters'
              />
            </Box>
            <Box sx={styles.actionsContainer}>
              <Button variant='outlined' sx={styles.actionButton} onClick={() => clearSearch()}>
                Clear
              </Button>
              <Button type='submit' variant='contained' sx={styles.actionButton}>
                Filter
              </Button>
            </Box>
          </Stack>
        </Paper>
      </form>
    </FormProvider>
  )
};

export default withErrorBoundary(Filters);
